import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    createRecoveryTask,
    updateRecoveryTask,
    getRecoveryTaskById,
    getRecoveryTasksByBCP,
    getUserDirectory,
} from "../../../service/api";
import axiosInstance from "../../../common/axiosInstance";

interface UserWithRole {
    id: string;
    name: string;
    employeeId: string;
    roleId: string;
    roleName: string;
}

const AddEditRecoveryTask: React.FC = () => {
    const navigate = useNavigate();
    const { planId, taskId } = useParams<{ planId: string; taskId?: string }>();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode") || "create";
    const isViewMode = mode === "view";
    const isEditMode = mode === "edit";

    const [formData, setFormData] = useState({
        taskName: "",
        description: "",
        priority: "Medium",
        status: "Pending",
        assignedUserId: "",
        startDate: "",
        targetDate: "",
        estimatedDuration: 0,
        dependencies: [] as string[],
        progress: 0,
    });

    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<UserWithRole[]>([]);
    const [availableTasks, setAvailableTasks] = useState<any[]>([]);

    useEffect(() => {
        fetchUsers();
        fetchAvailableTasks();
        if (taskId && (isEditMode || isViewMode)) {
            fetchTaskData();
        }
    }, [taskId]);

    const fetchUsers = async () => {
        try {
            const response = await getUserDirectory();
            const usersData = Array.isArray(response.data.data) ? response.data.data : [];
            const orgUsers = usersData.filter(
                (user) => user.roles?.includes("orguser") && !user.isDeleted
            );

            const usersWithRoles = await Promise.all(
                orgUsers.map(async (user) => {
                    const roleId =
                        typeof user.role === "string" ? user.role : user.role?.$oid || "";
                    let roleName = "Unknown";

                    if (roleId) {
                        try {
                            const roleResponse = await axiosInstance.get(`/api/roles/${roleId}`);
                            roleName = roleResponse.data.message.roleName || "Unknown";
                        } catch (error) {
                            console.error(`Failed to fetch role for user ${user._id}:`, error);
                        }
                    }

                    return {
                        id: user._id,
                        name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown",
                        employeeId: user.employeeId || "",
                        roleId,
                        roleName,
                    };
                })
            );

            setUsers(usersWithRoles);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Failed to fetch users");
        }
    };

    const fetchAvailableTasks = async () => {
        try {
            const response = await getRecoveryTasksByBCP(planId!);
            if (response.data?.resCode === 200 && response.data?.data) {
                const tasks = Array.isArray(response.data.data) ? response.data.data : [];
                // Filter out current task if editing
                const filtered = taskId
                    ? tasks.filter((t: any) => t._id !== taskId)
                    : tasks;
                setAvailableTasks(filtered);
            }
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    const fetchTaskData = async () => {
        try {
            const response = await getRecoveryTaskById(taskId!);
            if (response.data?.resCode === 200 && response.data?.data) {
                const task = response.data.data;
                setFormData({
                    taskName: task.taskName || "",
                    description: task.description || "",
                    priority: task.priority || "Medium",
                    status: task.status || "Pending",
                    assignedUserId: task.assignedUserId || "",
                    startDate: task.startDate ? task.startDate.split("T")[0] : "",
                    targetDate: task.targetDate ? task.targetDate.split("T")[0] : "",
                    estimatedDuration: task.estimatedDuration || 0,
                    dependencies: task.dependencies || [],
                    progress: task.progress || 0,
                });
            }
        } catch (error: any) {
            console.error("Error fetching recovery task:", error);
            toast.error(
                error.response?.data?.resMessage || "Failed to fetch recovery task"
            );
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDependencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
        setFormData((prev) => ({
            ...prev,
            dependencies: selectedOptions,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.taskName) {
            toast.error("Task name is required");
            return;
        }

        if (formData.targetDate && formData.startDate) {
            if (new Date(formData.targetDate) < new Date(formData.startDate)) {
                toast.error("Target date must be after start date");
                return;
            }
        }

        if (formData.progress < 0 || formData.progress > 100) {
            toast.error("Progress must be between 0 and 100");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                bcpPlanId: planId,
                taskName: formData.taskName,
                description: formData.description,
                priority: formData.priority,
                status: formData.status,
                assignedUserId: formData.assignedUserId || undefined,
                startDate: formData.startDate || undefined,
                targetDate: formData.targetDate || undefined,
                estimatedDuration: Number(formData.estimatedDuration) || undefined,
                dependencies: formData.dependencies.length > 0 ? formData.dependencies : undefined,
                progress: Number(formData.progress),
            };

            let response;
            if (isEditMode && taskId) {
                response = await updateRecoveryTask(taskId, payload);
            } else {
                response = await createRecoveryTask(payload);
            }

            if (response.data?.resCode === 200 || response.data?.resCode === 201) {
                toast.success(
                    response.data?.resMessage ||
                    `Recovery task ${isEditMode ? "updated" : "created"} successfully`
                );
                navigate(`/external-emergency-preparedness/bcp-plans/${planId}/recovery-tasks`);
            } else {
                throw new Error(
                    response.data?.resMessage ||
                    `Failed to ${isEditMode ? "update" : "create"} recovery task`
                );
            }
        } catch (error: any) {
            console.error("Error saving recovery task:", error);
            toast.error(
                error.response?.data?.resMessage ||
                error.message ||
                `Failed to ${isEditMode ? "update" : "create"} recovery task`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(`/external-emergency-preparedness/bcp-plans/${planId}/recovery-tasks`);
    };

    return (
        <div className="px-2 py-4 mt-14 w-full">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <h1 className="text-2xl font-semibold text-gray-900 mb-6">
                    {isViewMode
                        ? "View Recovery Task"
                        : isEditMode
                            ? "Edit Recovery Task"
                            : "Add Recovery Task"}
                </h1>

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            Basic Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Task Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="taskName"
                                    value={formData.taskName}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Priority
                                </label>
                                <select
                                    name="priority"
                                    value={formData.priority}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                >
                                    <option value="Critical">Critical</option>
                                    <option value="High">High</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Low">Low</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Blocked">Blocked</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Assignment & Timeline */}
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            Assignment & Timeline
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Assigned To
                                </label>
                                <select
                                    name="assignedUserId"
                                    value={formData.assignedUserId}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                >
                                    <option value="">Unassigned</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name} ({user.roleName}) - {user.employeeId}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Estimated Duration (hours)
                                </label>
                                <input
                                    type="number"
                                    name="estimatedDuration"
                                    value={formData.estimatedDuration}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    min="0"
                                    step="0.5"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Target Completion Date
                                </label>
                                <input
                                    type="date"
                                    name="targetDate"
                                    value={formData.targetDate}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Progress & Dependencies */}
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            Progress & Dependencies
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Progress (0-100%)
                                </label>
                                <input
                                    type="number"
                                    name="progress"
                                    value={formData.progress}
                                    onChange={handleInputChange}
                                    disabled={isViewMode}
                                    min="0"
                                    max="100"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dependencies (Hold Ctrl to select multiple)
                                </label>
                                <select
                                    multiple
                                    value={formData.dependencies}
                                    onChange={handleDependencyChange}
                                    disabled={isViewMode}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                                    size={4}
                                >
                                    {availableTasks.map((task) => (
                                        <option key={task._id} value={task._id}>
                                            {task.taskName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end gap-4 mt-6">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                        >
                            {isViewMode ? "Back" : "Cancel"}
                        </button>
                        {!isViewMode && (
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:bg-gray-400"
                            >
                                {loading
                                    ? "Saving..."
                                    : isEditMode
                                        ? "Update Task"
                                        : "Create Task"}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEditRecoveryTask;
